import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  indexSymbolsQueryOptions,
  signalSessionsQueryOptions,
  volatileQueryOptions,
} from '@/features/signals/api/queries'
import { VOLATILE_SEARCH_AVAILABLE } from '@/features/signals/api/signalsFlags'
import type { RouterContext } from '@/routes/layouts/root'
import {
  pagingParams,
  sessionDateParamSchema,
  symbolSearchParamSchema,
} from '@/screens/signals/search'

/**
 * Server paging, a symbol prefix (`q`, held behind `VOLATILE_SEARCH_AVAILABLE`), and the picker's `date`. The read takes none: it always covers the last 30
 * sessions, so a past `date` only turns the page to its no-data state
 * (`asks/signals-dates.md`). Fifteen a page, with a pager for the rest.
 */
export const volatileStocksSearchSchema = z.object({
  date: sessionDateParamSchema,
  q: symbolSearchParamSchema,
  ...pagingParams(),
})

/**
 * The `/market/signals/volatile` loader. MAIN: the entry URL's page.
 * SECONDARY: the index symbols (which rows link).
 */
export async function loadVolatileStocks({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const { page, pageSize, q } = volatileStocksSearchSchema.parse(location.search)
  if (!preload) {
    prefetchRouteData(queryClient, signalSessionsQueryOptions())
    prefetchRouteData(queryClient, indexSymbolsQueryOptions())
  }
  await ensureRouteData(
    queryClient,
    volatileQueryOptions({ page, pageSize, q: VOLATILE_SEARCH_AVAILABLE ? (q ?? null) : null }),
  )
}
